import dagre from "@dagrejs/dagre";
import {
	type AdminEpisodeDetail,
	type AdminNode,
	type EndingDefinition,
	type FlagEffect,
	FlagEffectOp,
	type RenderMode,
	type StoryGraphIssue,
} from "@storyline/shared";
import type { Edge, Node } from "@xyflow/react";

export type NodeOutcome = "decision" | "direct" | "ending" | "terminal";

export type StoryNodeData = {
	node: AdminNode;
	isEntry: boolean;
	renderModes: RenderMode[];
	outcome: NodeOutcome;
	endingName: string | null;
	issueCount: number;
	[key: string]: unknown;
};

export type EndingNodeData = {
	ending: EndingDefinition;
	[key: string]: unknown;
};

export type StoryFlowNodeItem = Node<StoryNodeData, "story">;
export type EndingFlowNodeItem = Node<EndingNodeData, "ending">;
export type StoryFlowNode = StoryFlowNodeItem | EndingFlowNodeItem;

export const storyNodeId = (nodeId: string): string => `node:${nodeId}`;
export const endingNodeId = (endingId: string): string => `ending:${endingId}`;

const STORY_NODE_WIDTH = 240;
const STORY_NODE_HEIGHT = 150;
const ENDING_NODE_WIDTH = 200;
const ENDING_NODE_HEIGHT = 80;

const outcomeOf = (node: AdminNode): NodeOutcome => {
	if (node.decision) {
		return "decision";
	}
	if (node.nextNodeId !== null) {
		return "direct";
	}
	if (node.endingId !== null) {
		return "ending";
	}
	return "terminal";
};

export const effectLabel = (effect: FlagEffect): string => {
	if (effect.op === FlagEffectOp.ADD) {
		const amount = typeof effect.value === "number" && effect.value >= 0 ? `+${effect.value}` : String(effect.value);
		return `${effect.flag} ${amount}`;
	}
	return `${effect.flag} = ${String(effect.value)}`;
};

const edgeLabel = (label: string, effects: FlagEffect[]): string => {
	if (effects.length === 0) {
		return label;
	}
	return `${label} · ${effects.map(effectLabel).join(", ")}`;
};

export const resolveSubjectLabel = (episode: AdminEpisodeDetail, subjectId: string | null): string => {
	if (!subjectId) {
		return "Graphe";
	}
	for (const node of episode.nodes) {
		if (node.id === subjectId) {
			return `Nœud « ${node.title} »`;
		}
		for (const shot of node.shots) {
			if (shot.id === subjectId) {
				return `Plan de « ${node.title} »`;
			}
		}
		if (node.decision) {
			if (node.decision.id === subjectId) {
				return `Décision de « ${node.title} »`;
			}
			for (const choice of node.decision.choices) {
				if (choice.id === subjectId) {
					return `Choix « ${choice.label} »`;
				}
			}
		}
	}
	const ending = episode.endings.find((candidate) => candidate.id === subjectId);
	if (ending) {
		return `Fin « ${ending.name} »`;
	}
	const flag = episode.flags.find((candidate) => candidate.name === subjectId);
	if (flag) {
		return `Flag « ${flag.name} »`;
	}
	return subjectId;
};

export const buildEpisodeFlow = (episode: AdminEpisodeDetail, issues: StoryGraphIssue[]): { nodes: StoryFlowNode[]; edges: Edge[] } => {
	const endingNameById = new Map(episode.endings.map((ending) => [ending.id, ending.name]));
	const issueCountBySubject = new Map<string, number>();
	for (const issue of issues) {
		if (issue.subjectId) {
			issueCountBySubject.set(issue.subjectId, (issueCountBySubject.get(issue.subjectId) ?? 0) + 1);
		}
	}

	const edges: Edge[] = [];
	for (const node of episode.nodes) {
		if (node.decision) {
			for (const choice of node.decision.choices) {
				edges.push({
					id: `choice:${choice.id}`,
					source: storyNodeId(node.id),
					target: storyNodeId(choice.nextNodeId),
					label: edgeLabel(choice.label, choice.effects),
					animated: choice.id === node.decision.defaultChoiceId,
				});
			}
		} else if (node.nextNodeId !== null) {
			edges.push({ id: `direct:${node.id}`, source: storyNodeId(node.id), target: storyNodeId(node.nextNodeId), animated: true });
		} else if (node.endingId !== null) {
			edges.push({
				id: `ending:${node.id}`,
				source: storyNodeId(node.id),
				target: endingNodeId(node.endingId),
				style: { strokeDasharray: "6 4" },
			});
		}
	}

	const storyNodes: StoryFlowNode[] = episode.nodes.map((node) => ({
		id: storyNodeId(node.id),
		type: "story",
		position: { x: 0, y: 0 },
		data: {
			node,
			isEntry: node.id === episode.entryNodeId,
			renderModes: node.shots.map((shot) => shot.renderMode),
			outcome: outcomeOf(node),
			endingName: node.endingId ? (endingNameById.get(node.endingId) ?? null) : null,
			issueCount: issueCountBySubject.get(node.id) ?? 0,
		},
	}));

	const endingNodes: StoryFlowNode[] = episode.endings.map((ending) => ({
		id: endingNodeId(ending.id),
		type: "ending",
		position: { x: 0, y: 0 },
		data: { ending },
	}));

	const layout = new dagre.graphlib.Graph();
	layout.setDefaultEdgeLabel(() => ({}));
	layout.setGraph({ rankdir: "TB", nodesep: 56, ranksep: 80 });
	for (const node of storyNodes) {
		layout.setNode(node.id, { width: STORY_NODE_WIDTH, height: STORY_NODE_HEIGHT });
	}
	for (const node of endingNodes) {
		layout.setNode(node.id, { width: ENDING_NODE_WIDTH, height: ENDING_NODE_HEIGHT });
	}
	for (const edge of edges) {
		layout.setEdge(edge.source, edge.target);
	}
	dagre.layout(layout);

	const place = (node: StoryFlowNode, width: number, height: number): StoryFlowNode => {
		const placed = layout.node(node.id);
		return { ...node, position: { x: placed.x - width / 2, y: placed.y - height / 2 } };
	};

	return {
		nodes: [
			...storyNodes.map((node) => place(node, STORY_NODE_WIDTH, STORY_NODE_HEIGHT)),
			...endingNodes.map((node) => place(node, ENDING_NODE_WIDTH, ENDING_NODE_HEIGHT)),
		],
		edges,
	};
};
